import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
    Copy,
    Check,
    Download,
    Upload,
    Minimize2,
    Maximize2,
    AlertCircle,
    Braces,
    Trash2,
} from "lucide-react";

// ── Helpers ──────────────────────────────────────────────────

function formatJSON(input: string, indent: number): string {
    return JSON.stringify(JSON.parse(input), null, indent);
}

function minifyJSON(input: string): string {
    return JSON.stringify(JSON.parse(input));
}

interface ValidationResult {
    valid: boolean;
    error?: string;
    line?: number;
    column?: number;
}

function validateJSON(input: string): ValidationResult {
    try {
        JSON.parse(input);
        return { valid: true };
    } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : "Invalid JSON";
        // Try to extract line/column from error message
        const posMatch = msg.match(/position (\d+)/);
        if (posMatch) {
            const pos = parseInt(posMatch[1]!);
            const before = input.slice(0, pos);
            const line = (before.match(/\n/g) || []).length + 1;
            const column = pos - before.lastIndexOf("\n");
            return { valid: false, error: msg, line, column };
        }
        return { valid: false, error: msg };
    }
}

// ── Component ────────────────────────────────────────────────

export function JsonFormatter() {
    const [input, setInput] = useState("");
    const [output, setOutput] = useState("");
    const [indent, setIndent] = useState(2);
    const [error, setError] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);
    const [mode, setMode] = useState<"format" | "minify" | "validate">("format");

    useEffect(() => {
        document.title =
            "JSON Formatter & Validator — Beautify, Minify & Validate JSON — Ballah Code";
        const meta = document.querySelector('meta[name="description"]');
        if (meta) {
            meta.setAttribute(
                "content",
                "Free JSON formatter, beautifier, minifier and validator. Paste your JSON to format, minify, or validate instantly. No data sent to any server.",
            );
        }
    }, []);

    const processJSON = useCallback(() => {
        if (!input.trim()) {
            setOutput("");
            setError(null);
            return;
        }

        const validation = validateJSON(input);

        if (mode === "validate") {
            if (validation.valid) {
                setOutput("✓ Valid JSON");
                setError(null);
            } else {
                setOutput("");
                setError(
                    validation.line
                        ? `${validation.error} (line ${validation.line}, col ${validation.column})`
                        : validation.error || "Invalid JSON",
                );
            }
            return;
        }

        if (!validation.valid) {
            setError(
                validation.line
                    ? `${validation.error} (line ${validation.line}, col ${validation.column})`
                    : validation.error || "Invalid JSON",
            );
            setOutput("");
            return;
        }

        setError(null);
        try {
            setOutput(mode === "minify" ? minifyJSON(input) : formatJSON(input, indent));
        } catch {
            setError("Failed to process JSON");
        }
    }, [input, mode, indent]);

    // Auto-process on change
    useEffect(() => {
        const timer = setTimeout(processJSON, 150);
        return () => clearTimeout(timer);
    }, [processJSON]);

    const copyToClipboard = async () => {
        await navigator.clipboard.writeText(output);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const downloadJSON = () => {
        const blob = new Blob([output], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "formatted.json";
        a.click();
        URL.revokeObjectURL(url);
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            setInput(ev.target?.result as string);
        };
        reader.readAsText(file);
        e.target.value = "";
    };

    const stats = input.trim()
        ? (() => {
            try {
                const parsed = JSON.parse(input);
                const keys = typeof parsed === "object" && parsed !== null
                    ? Object.keys(parsed).length
                    : 0;
                const size = new Blob([input]).size;
                return { keys, size, valid: true };
            } catch {
                return { keys: 0, size: new Blob([input]).size, valid: false };
            }
        })()
        : null;

    return (
        <div className="min-h-screen bg-background text-foreground font-sans">
            {/* Nav */}
            <nav className="flex items-center justify-between px-8 py-[18px]">
                <Link
                    to="/"
                    className="text-sm font-semibold tracking-[-0.03em] text-zinc-200 hover:text-white transition-colors"
                >
                    ballah code
                </Link>
                <div className="flex items-center gap-6 text-[13px] text-zinc-600">
                    <Link to="/tools" className="hover:text-zinc-400 transition-colors">
                        tools
                    </Link>
                    <Link to="/" className="hover:text-zinc-400 transition-colors">
                        home
                    </Link>
                </div>
            </nav>

            <main className="max-w-[1280px] mx-auto px-8 pt-12 pb-24">
                {/* Breadcrumb */}
                <div className="flex items-center gap-2 text-[13px] text-zinc-600 mb-8">
                    <Link to="/tools" className="hover:text-zinc-400 transition-colors">
                        Tools
                    </Link>
                    <span className="text-zinc-700">/</span>
                    <span className="text-zinc-400">JSON Formatter</span>
                </div>

                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-medium tracking-[-0.03em] mb-3">
                        JSON Formatter & Validator
                    </h1>
                    <p className="text-zinc-500 text-[15px] leading-relaxed max-w-2xl">
                        Paste or upload JSON to beautify, minify, or validate. Everything
                        runs in your browser — nothing is sent to any server.
                    </p>
                </div>

                {/* Controls bar */}
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                    {/* Mode buttons */}
                    <div className="flex rounded-lg border border-white/[0.06] overflow-hidden">
                        {(
                            [
                                { key: "format", label: "Format", icon: Maximize2 },
                                { key: "minify", label: "Minify", icon: Minimize2 },
                                { key: "validate", label: "Validate", icon: AlertCircle },
                            ] as const
                        ).map(({ key, label, icon: Icon }) => (
                            <button
                                key={key}
                                onClick={() => setMode(key)}
                                className={`px-3.5 py-2 text-[12px] flex items-center gap-1.5 transition-colors ${mode === key
                                    ? "bg-white/[0.08] text-zinc-200"
                                    : "text-zinc-600 hover:text-zinc-400"
                                    }`}
                            >
                                <Icon size={12} />
                                {label}
                            </button>
                        ))}
                    </div>

                    {/* Indent selector */}
                    {mode === "format" && (
                        <div className="flex items-center gap-1.5 text-[12px] text-zinc-600 ml-2">
                            <span>Indent:</span>
                            {[2, 4].map((n) => (
                                <button
                                    key={n}
                                    onClick={() => setIndent(n)}
                                    className={`px-2 py-1 rounded text-[12px] transition-colors ${indent === n
                                        ? "bg-white/[0.08] text-zinc-300"
                                        : "text-zinc-600 hover:text-zinc-400"
                                        }`}
                                >
                                    {n}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* File upload */}
                    <label className="ml-auto flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/[0.06] text-[12px] text-zinc-500 hover:text-zinc-300 hover:border-white/[0.1] cursor-pointer transition-colors">
                        <Upload size={12} />
                        Upload
                        <input
                            type="file"
                            accept=".json,.txt"
                            onChange={handleFileUpload}
                            className="hidden"
                        />
                    </label>

                    {/* Clear */}
                    {input && (
                        <button
                            onClick={() => {
                                setInput("");
                                setOutput("");
                                setError(null);
                            }}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/[0.06] text-[12px] text-zinc-600 hover:text-zinc-400 transition-colors"
                        >
                            <Trash2 size={12} />
                            Clear
                        </button>
                    )}
                </div>

                {/* Editor panels */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                    {/* Input */}
                    <div className="flex flex-col">
                        <div className="flex items-center justify-between px-4 py-2.5 bg-[#0d0d0f] border border-white/[0.06] border-b-0 rounded-t-xl">
                            <span className="text-[11px] text-zinc-600 font-mono uppercase tracking-wider">
                                Input
                            </span>
                            {stats && (
                                <span className="text-[11px] text-zinc-700">
                                    {(stats.size / 1024).toFixed(1)}KB
                                </span>
                            )}
                        </div>
                        <textarea
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder='Paste JSON here...\n\n{"example": "value"}'
                            spellCheck={false}
                            className="w-full min-h-[400px] bg-[#111113] border border-white/[0.06] rounded-b-xl p-4 text-[13px] text-zinc-300 font-mono leading-relaxed outline-none resize-y placeholder:text-zinc-800 focus:border-white/[0.1] transition-colors"
                        />
                    </div>

                    {/* Output */}
                    <div className="flex flex-col">
                        <div className="flex items-center justify-between px-4 py-2.5 bg-[#0d0d0f] border border-white/[0.06] border-b-0 rounded-t-xl">
                            <span className="text-[11px] text-zinc-600 font-mono uppercase tracking-wider">
                                Output
                            </span>
                            <div className="flex items-center gap-1">
                                {output && (
                                    <>
                                        <button
                                            onClick={copyToClipboard}
                                            className="flex items-center gap-1 px-2 py-1 rounded text-[11px] text-zinc-600 hover:text-zinc-300 transition-colors"
                                        >
                                            {copied ? (
                                                <Check size={11} className="text-green-400" />
                                            ) : (
                                                <Copy size={11} />
                                            )}
                                            {copied ? "Copied" : "Copy"}
                                        </button>
                                        <button
                                            onClick={downloadJSON}
                                            className="flex items-center gap-1 px-2 py-1 rounded text-[11px] text-zinc-600 hover:text-zinc-300 transition-colors"
                                        >
                                            <Download size={11} />
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                        <div className="w-full min-h-[400px] bg-[#111113] border border-white/[0.06] rounded-b-xl p-4 text-[13px] font-mono leading-relaxed overflow-auto">
                            {error ? (
                                <div className="flex items-start gap-2 text-red-400">
                                    <AlertCircle
                                        size={14}
                                        className="mt-0.5 shrink-0"
                                    />
                                    <span>{error}</span>
                                </div>
                            ) : output ? (
                                <pre className="text-zinc-300 whitespace-pre-wrap break-all">
                                    {output}
                                </pre>
                            ) : (
                                <span className="text-zinc-800">
                                    Output will appear here...
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Stats bar */}
                {stats && (
                    <div className="flex items-center gap-6 text-[12px] text-zinc-600 mb-16">
                        <span className="flex items-center gap-1.5">
                            <Braces size={12} />
                            {stats.valid ? "Valid JSON" : "Invalid JSON"}
                        </span>
                        {stats.valid && (
                            <span>
                                {stats.keys} top-level{" "}
                                {stats.keys === 1 ? "key" : "keys"}
                            </span>
                        )}
                        <span>{(stats.size / 1024).toFixed(1)} KB</span>
                    </div>
                )}

                {/* SEO content */}
                <section className="max-w-2xl border-t border-white/[0.04] pt-12">
                    <h2 className="text-lg font-medium tracking-[-0.02em] mb-4 text-zinc-300">
                        What is JSON formatting?
                    </h2>
                    <div className="space-y-4 text-[14.5px] text-zinc-500 leading-[1.75]">
                        <p>
                            JSON (JavaScript Object Notation) is the most common data format
                            for APIs, configuration files, and data exchange. JSON formatting
                            takes compact or unreadable JSON and adds proper indentation and
                            line breaks to make it human-readable.
                        </p>
                        <p>
                            This tool also validates your JSON against the official spec,
                            highlighting syntax errors with line and column numbers. You can
                            minify JSON to reduce file size, or beautify it for readability.
                        </p>
                        <p>
                            Everything runs entirely in your browser — no data is ever sent
                            to a server. Built by the team behind{" "}
                            <Link
                                to="/"
                                className="text-violet-400 hover:text-violet-300 transition-colors"
                            >
                                Ballah Code
                            </Link>
                            .
                        </p>
                    </div>
                    <div className="mt-8 flex flex-wrap gap-2">
                        <span className="text-[12px] text-zinc-700 mr-1 self-center">Related →</span>
                        <Link to="/tools/jwt-decoder" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">JWT Decoder</Link>
                        <Link to="/tools/diff-checker" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">Diff Checker</Link>
                        <Link to="/tools/base64" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">Base64 Encode / Decode</Link>
                        <Link to="/tools/regex-tester" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">Regex Tester</Link>
                    </div>
                </section>
            </main>

            {/* Footer */}
            <footer className="border-t border-white/[0.04] py-5 px-8 flex justify-between max-w-[1280px] mx-auto text-xs text-zinc-800">
                <Link to="/" className="hover:text-zinc-600 transition-colors">
                    ballah code
                </Link>
                <Link to="/tools" className="hover:text-zinc-600 transition-colors">
                    tools
                </Link>
                <span>ballah tech</span>
            </footer>
        </div>
    );
}
